/**
 * @voltxampere/fx — LED brightness + color helpers (Sprint 31-2)
 *
 * Map a diode's forward current to a normalised brightness value
 * [0, 1] via log-scale interpolation between a noise floor (100µA)
 * and a saturation ceiling (100mA). Three decades of headroom matches
 * the dynamic range a typical LED covers (sub-threshold leakage to
 * destructive max-rated current) and gives the user perceptual
 * proportionality: each 10× increase in forward current adds the
 * SAME visual brightness step.
 *
 * Sentinel rows pinned by tests:
 *   I_forward = 0          → 0   (LED off)
 *   I_forward < 100 µA     → 0   (below noise floor / sub-threshold)
 *   I_forward = 100 µA     → 0   (at floor, edge case)
 *   I_forward = 20 mA      → ~0.77 (typical bright LED operating point)
 *   I_forward = 100 mA     → 1   (saturation ceiling)
 *   I_forward > 100 mA     → 1   (clamped — would be destructive in real LED)
 *   I_forward < 0          → 0   (reverse current, no light emission)
 *
 * Renderer composition (Sprint 31-6 wires this in):
 *   glow_radius = 12 + 24 × brightness   (12..36 css px world-units)
 *   glow_alpha  = brightness             (overlay screen blend)
 *   glow_color  = ledColorHex(led.color)
 *
 * The color helper maps the LED's `color` enum (red/green/blue/yellow/
 * white — Sprint 23 B1 schema) to a hex value the canvas renderer
 * passes to `createRadialGradient`. Unset color defaults to red.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const I_FORWARD_THRESHOLD = 1e-4; // 100 µA — below this LED is off
const I_FORWARD_MAX = 0.1; // 100 mA — saturated max brightness
/** log10(I_FORWARD_MAX / I_FORWARD_THRESHOLD) = log10(1000) = 3, computed once. */
const LOG_DENOM = 3;
/**
 * Compute the log-scaled, [0, 1]-clamped LED brightness for a diode
 * carrying `I_forward` amperes (positive forward current). Caller
 * should pre-`Math.max(0, I)` to drop reverse current — LEDs do not
 * emit light when reverse-biased.
 */
export function ledBrightnessFromCurrent(I_forward) {
    if (I_forward <= I_FORWARD_THRESHOLD)
        return 0;
    if (I_forward >= I_FORWARD_MAX)
        return 1;
    const ratio = Math.log10(I_forward / I_FORWARD_THRESHOLD) / LOG_DENOM;
    return Math.max(0, Math.min(1, ratio));
}
/**
 * Resolve LED color enum to canvas hex. Defaults to red when the
 * caller passes `undefined` (matches `LedParamsSchema.color.default`).
 *
 * Color values picked to match Mockup §E LED emission palette — vivid
 * primaries with slight desaturation so the glow reads as warm light
 * rather than pure RGB.
 */
export function ledColorHex(color) {
    switch (color ?? "red") {
        case "red":
            return "#FF3344";
        case "green":
            return "#44FF55";
        case "blue":
            return "#4477FF";
        case "yellow":
            return "#FFDD33";
        case "white":
            return "#FFFFEE";
    }
}
//# sourceMappingURL=led-brightness.js.map